import { Injectable } from '@angular/core';
import { Crop } from '@next/arable-lib/crop';
import { CropField } from '@next/arable-lib/crop-field';

import { DisplayAdvice, AdviceResponse, IssueType, RiskLevel, ActionNecessity } from '../models/models';

@Injectable({
  providedIn: 'root',
})
export class TgaSharedService {
  constructor() { }

  getCropName(cropId: number | undefined, crops: Crop[]): string {
    if (!cropId) return '';
    const crop = crops.find((c) => c.id === cropId);
    return crop?.name?.toLowerCase() || '';
  }

  getEndDateYear(cropField: CropField, fallbackYear: number): number {
    if (cropField.endDate) {
      return new Date(cropField.endDate).getFullYear();
    }
    return fallbackYear;
  }

  toNumber(value: string | number): number | null {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const n = Number.parseInt(value, 10);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  }

  mapAdviceList(gaList?: AdviceResponse[] | null): DisplayAdvice[] {
    if (!gaList?.length) return [];

    const filteredAdvices = gaList.filter(this.isValidAdvice);
    if (!filteredAdvices.length) return [];

    const processedAdvices = filteredAdvices.flatMap((advice) => this.processAdviceResponse(advice));
    return processedAdvices;
  }

  /**
   * Transforms flat translation parameters with dotted keys into a nested object structure.
   * Supports multiple levels of nesting.
   * Examples:
   * - { "VarietyProperties.VroegheidRas": "6.2" } => { VarietyProperties: { VroegheidRas: "6.2" } }
   * - { "VarietyProperties.standards.common.Schurft": "value" } => { VarietyProperties: { standards: { common: { Schurft: "value" } } } }
   */
  transformTranslationParams(flatParams: Record<string, string>): Record<string, string | Record<string, unknown>> {
    const result: Record<string, string | Record<string, unknown>> = {};

    for (const [key, value] of Object.entries(flatParams)) {
      if (key.includes('.')) {
        const parts = key.split('.');
        let current: Record<string, unknown> = result as Record<string, unknown>;

        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part] || typeof current[part] !== 'object') {
            current[part] = {};
          }
          current = current[part] as Record<string, unknown>;
        }

        current[parts.at(-1)!] = value;
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  private isValidAdvice(advice: AdviceResponse): boolean | undefined {
    const hasMeasures = advice.measures?.length > 0;
    const hasValidAdviceItems = advice.adviceItems?.some(
      (item) => item.translationCode?.trim().length > 0,
    );
    return hasMeasures || hasValidAdviceItems;
  }

  private processAdviceResponse(advice: AdviceResponse): DisplayAdvice[] {
    const measures = (advice.measures ?? []).filter((m) => (m?.translationCode ?? '') !== '-');
    const adviceItems = advice.adviceItems ?? [];

    const processedMeasures = this.processItemsWithIssueTypes(measures, [...measures, ...adviceItems]);
    const processedAdviceItems = this.processItemsWithIssueTypes(adviceItems, [...measures, ...adviceItems]);

    const issueTypes = new Set(processedAdviceItems.map((a) => a.issueType));

    const subjectTransCode = this.normalizeSubjectTransCode(advice.subjectTransCode);

    return Array.from(issueTypes).map((issueType) => ({
      subjectTransCode,
      riskOfDamage: this.mapCodesToRiskLevelEnum(advice.riskOfDamage),
      necessityForAction: this.mapCodesToActionNecessityEnum(advice.necessityForAction),
      issueType: issueType ? this.getEnumIssueType(issueType) : '' as IssueType,
      measure: processedMeasures,
      adviceItem: processedAdviceItems.filter((a) => a.issueType === issueType),
      soilVarietyDifferences: advice.soilVarietyDifferences,
    }));
  }

  private processItemsWithIssueTypes<T extends { translationCode?: string; issueType?: string }>(
    items: T[],
    allItems: Array<{ translationCode?: string; issueType?: string }>,
  ): Array<T & { issueType: string; generic?: boolean }> {
    const itemsWithIssueType = allItems.find((item) => item.issueType);
    const fallbackIssueType = itemsWithIssueType?.issueType ?? '';

    return items.map((item) => {
      let issueType = item.issueType ?? '';
      let generic = false;

      if (this.isSpecialTranslationCode(item.translationCode)) {
        generic = true;
        issueType = issueType || fallbackIssueType;
      }

      return { ...item, issueType, ...(generic && { generic }) };
    });
  }

  private isSpecialTranslationCode(translationCode?: string): boolean {
    const specialCodes = [
      'COMMON_NO_MEASURE',
      'COMMON_NO_MEASURES',
      'COMMON_STANDARD_ADVICE',
      'COMMON_RESEARCH_OLD',
      'COMMON_RESEARCH_UNKNOWN',
      'COMMON_RESEARCH_NOT_DONE',
      'COMMON_RESEARCH_PERFORMED_NO_RESULT',
      'COMMON_NO_RISK',
    ];
    return specialCodes.includes(translationCode ?? '');
  }

  private normalizeSubjectTransCode(subjectTransCode?: string): string {
    if (subjectTransCode === 'RHG' || subjectTransCode === 'RHK') {
      return 'RH';
    }
    return subjectTransCode ?? '';
  }

  private getEnumIssueType(apiIssueType: string): IssueType {
    return IssueType[apiIssueType as keyof typeof IssueType] ?? apiIssueType as IssueType;
  }

  private mapCodesToRiskLevelEnum(riskCode: string): RiskLevel {
    switch (riskCode) {
      case 'COMMON_RISK_NONE':
        return RiskLevel.No;
      case 'COMMON_RISK_LOW':
      case 'COMMON_RISK_PRESENT':
      case 'COMMON_RISK_MINOR':
        return RiskLevel.Present;
      case 'COMMON_RISK_LIKELY':
        return RiskLevel.Likely;
      case 'COMMON_RISK_VERY_LIKELY':
        return RiskLevel.VeryLikely;
      case 'COMMON_RISK_HIGH':
      case 'COMMON_RISK_VERY_HIGH':
        return RiskLevel.High;
      case 'COMMON_RISK_UNKNOWN':
        return RiskLevel.Unknown;
      default:
        return RiskLevel.Unknown;
    }
  }

  private mapCodesToActionNecessityEnum(actionCode: string): ActionNecessity {
    switch (actionCode) {
      case 'COMMON_NECESSITY_NONE':
        return ActionNecessity.No;
      case 'COMMON_NECESSITY_MINOR':
      case 'COMMON_NECESSITY_PRESENT':
        return ActionNecessity.Present;
      case 'COMMON_NECESSITY_LIKELY':
      case 'COMMON_NECESSITY_DESIRED':
        return ActionNecessity.Desired;
      case 'COMMON_NECESSITY_VERY_LIKELY':
      case 'COMMON_NECESSITY_VERY_DESIRED':
        return ActionNecessity.VeryDesired;
      case 'COMMON_NECESSITY_HIGH':
      case 'COMMON_NECESSITY_VERY_HIGH':
        return ActionNecessity.High;
      case 'COMMON_NECESSITY_UNKNOWN':
        return ActionNecessity.Unknown;
      default:
        return ActionNecessity.Unknown;
    }
  }
}
